import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Upload } from "lucide-react";
import { api, errMsg, money } from "@/lib/api";
import { Field, NSelect } from "@/components/common";
import { Input } from "@/components/ui/input";

const FORMATIONS = ["Formation en informatique", "Installation de caméras de surveillance"];
const EMPTY = { name: "", phone: "", email: "", formation: FORMATIONS[0], address: "", modules: 1, website: "" };
const quote = (n) => [["Inscription", 10], ["Syllabus", 25], ["Gilet avec logo ACES (obligatoire)", 15], [`Formation : ${n} module(s) × 80 USD`, 80 * n], ["Certificat", 20]];

const QuoteBox = ({ n }) => {
  const rows = quote(n);
  return (
    <div className="rounded-md border border-slate-200 bg-white p-5" data-testid="training-quote">
      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-red-600">Votre devis</p>
      {rows.map(([l, a]) => <div key={l} className="flex justify-between border-b border-slate-100 py-1.5 text-sm"><span>{l}</span><span>{money(a)}</span></div>)}
      <div className="mt-2 flex justify-between font-display text-lg font-bold"><span>Total</span><span data-testid="training-quote-total">{money(rows.reduce((s, [, a]) => s + a, 0))}</span></div>
    </div>
  );
};

export default function TrainingSignup() {
  const [f, setF] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!photo) return setError("Merci d'ajouter votre photo.");
    setError("");
    setLoading(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    fd.append("photo", photo);
    try {
      const r = await api.post("/public/training", fd);
      setDone(r.data);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid-bg min-h-screen bg-slate-100">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-20 max-w-5xl items-center justify-between px-5">
          <Link to="/"><img src="/logo.png" alt="ACES" className="h-12 w-auto" /></Link>
          <Link to="/" className="text-sm font-semibold text-slate-600 hover:text-red-600" data-testid="training-back-home">← Retour au site</Link>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-5 py-12">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-red-600">Inscription</p>
        <h1 className="mt-2 font-display text-3xl font-bold text-slate-900 sm:text-4xl">Formation informatique & installation de caméras</h1>
        <p className="mt-3 max-w-2xl text-sm text-slate-600">Complétez ce formulaire. Après validation de votre dossier, notre équipe vous communique le numéro mobile pour le paiement, puis vous recevez la grille de formation par email (samedi et dimanche 14h–16h, séance spéciale le vendredi 17h–18h30).</p>
        {done ? (
          <div className="mt-8 rounded-md bg-white p-10 text-center" data-testid="training-success">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
            <h2 className="mt-4 font-display text-2xl font-bold">Inscription envoyée</h2>
            <p className="mt-2 text-sm text-slate-600">Votre dossier <b className="font-mono">{done.number}</b> est en cours d'étude. Montant estimé : <b>{money(done.quote.total)}</b>. Nous vous contactons très bientôt.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 grid gap-6 lg:grid-cols-[1fr_320px]" data-testid="training-form">
            <div className="grid gap-4 rounded-md bg-white p-6 sm:grid-cols-2">
              <Field label="Nom complet *"><Input required minLength={2} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="tr-name-input" /></Field>
              <Field label="Téléphone *"><Input required minLength={6} value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+243 …" data-testid="tr-phone-input" /></Field>
              <Field label="Email * (pour recevoir le programme)" className="sm:col-span-2"><Input type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="tr-email-input" /></Field>
              <Field label="Intitulé de la formation *"><NSelect value={f.formation} onChange={(v) => set("formation", v)} testId="tr-formation-select">{FORMATIONS.map((x) => <option key={x}>{x}</option>)}</NSelect></Field>
              <Field label="Nombre de modules"><Input type="number" min={1} max={10} value={f.modules} onChange={(e) => set("modules", Math.max(1, Math.min(10, Number(e.target.value) || 1)))} data-testid="tr-modules-input" /></Field>
              <Field label="Adresse *" className="sm:col-span-2"><Input required minLength={3} value={f.address} onChange={(e) => set("address", e.target.value)} data-testid="tr-address-input" /></Field>
              <Field label="Photo * (JPG, PNG, WEBP — 5 Mo max)" className="sm:col-span-2">
                <label className="flex cursor-pointer items-center gap-3 rounded-md border border-dashed border-slate-300 p-4 text-sm text-slate-600 hover:border-slate-500">
                  <Upload className="h-4 w-4" /> {photo ? photo.name : "Choisir une photo"}
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => setPhoto(e.target.files[0] || null)} data-testid="tr-photo-input" />
                </label>
              </Field>
              <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => set("website", e.target.value)} className="hidden" aria-hidden="true" />
              {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 sm:col-span-2" data-testid="tr-error">{error}</p>}
              <button type="submit" disabled={loading} className="flex h-12 items-center justify-center gap-2 rounded-md bg-red-600 px-6 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60 sm:col-span-2 sm:justify-self-start" data-testid="tr-submit-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Envoyer mon inscription"}
              </button>
            </div>
            <aside className="h-fit lg:sticky lg:top-6"><QuoteBox n={f.modules} /></aside>
          </form>
        )}
      </main>
    </div>
  );
}
